import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const TeamPhoto = ({ member, size = 'md' }) => {
  const [failed, setFailed] = useState(false);
  const sizeClass =
    size === 'lg' ? 'w-36 h-36 text-3xl' : 'w-28 h-28 text-2xl';

  if (failed || !member.photo) {
    return (
      <div
        className={`${sizeClass} rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold mx-auto mb-5 flex-shrink-0`}
        aria-hidden="true"
      >
        {member.initials}
      </div>
    );
  }

  return (
    <img
      src={member.photo}
      alt={member.name}
      className={`${sizeClass} rounded-full object-cover object-top mx-auto mb-5 flex-shrink-0 border-4 border-white shadow-md`}
      onError={() => setFailed(true)}
    />
  );
};

const TeamMemberCard = ({ member, leadership = false }) => (
  <article
    className={`bg-background-auxiliary rounded-2xl border border-gray-100/50 hover:border-primary/30 hover:shadow-md transition-shadow h-full flex flex-col ${
      leadership ? 'p-8 md:p-10' : 'p-8'
    }`}
  >
    <TeamPhoto member={member} size={leadership ? 'lg' : 'md'} />
    <div className="flex flex-col flex-grow text-center">
      <div className="flex items-center justify-center gap-3 mb-1">
        <h3 className={`font-bold text-text ${leadership ? 'text-xl md:text-2xl' : 'text-xl'}`}>
          {member.name}
        </h3>
        {member.linkedin && (
          <a
            href={member.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-hover flex-shrink-0"
            aria-label={`${member.name} on LinkedIn`}
          >
            <LinkedInIcon />
          </a>
        )}
      </div>
      <p className="text-primary font-semibold text-sm mb-4">{member.role}</p>
      <p className="text-text/70 leading-relaxed text-base mb-3 text-left flex-grow">
        {member.bio}
      </p>
      {member.education && (
        <p className="text-text/60 text-sm text-left">
          <span className="font-semibold text-text/70">Education:</span> {member.education}
        </p>
      )}
      {member.tenure && (
        <p className="text-text/60 text-sm mt-1 text-left">
          <span className="font-semibold text-text/70">Tenure:</span> {member.tenure}
        </p>
      )}
    </div>
  </article>
);

const Team = ({ showCta = true, transparentBackground = false }) => {
  const leadership = [
    {
      name: 'Mauricio Reis',
      role: 'Founder & Managing Director',
      bio: "Founder of TM RUSH and CEO of Marcas Já, with 11+ years of operational experience in the Brazilian trademark sector. Mauricio's procedural foundation was built under the direct mentorship of a former President of the Brazilian BPTO (INPI), giving unique insight into how the office thinks and operates, which translates into faster filings and higher pre-vetting accuracy for every client.",
      education: 'ESPM, Escola Superior de Propaganda e Marketing',
      tenure: '11+ years in the Brazilian IP sector',
      photo: '/images/team/mau.jpg',
      initials: 'MR',
      linkedin: null,
    },
    {
      name: 'Gabriele Borges Granemann',
      role: 'Co-Founder & Head of Global Expansion',
      bio: 'Co-Founder of Marcas Já and TM RUSH. Gabriele leads international relations, partnerships and the global expansion strategy of the firm. Her work focuses on building trusted bridges between TM RUSH and international IP firms, corporate clients and IP correspondents around the world.',
      education: 'PUCRS, Pontifícia Universidade Católica do Rio Grande do Sul (Law graduate)',
      tenure: 'Co-Founder since inception',
      photo: '/images/team/gabi.jpg',
      initials: 'GG',
      linkedin: null,
    },
  ];

  const members = [
    {
      name: 'Caroline Tessmer',
      role: 'Operations Manager, Customer Success & IP Operations',
      bio: 'Carol leads the operations engine of Marcas Já and TM RUSH. She is the central node ensuring that every filing instruction received from a domestic or international partner becomes a clean, on-time protocol at the BPTO, so no deadline, office action or opposition slips through the cracks.',
      tenure: '9+ years in the Brazilian IP sector',
      photo: '/images/team/carol.jpg',
      initials: 'CT',
      linkedin: null,
    },
    {
      name: 'Natanael Tessmer',
      role: 'Global Inbound Executive & IP Consultant',
      bio: 'Natanael is the first point of contact for international firms and companies reaching out to TM RUSH. He runs the inbound diagnostic (viability searches, classification, and strategic assessment of each new instruction), ensuring every case starts with a clean Go / No-Go report before filing.',
      tenure: 'First point of contact for inbound clients',
      photo: '/images/team/nata.jpg',
      initials: 'NT',
      linkedin: null,
    },
    {
      name: 'Gabriellen Sobrero (Belle)',
      role: 'Account Manager & Administrative Compliance',
      bio: 'Belle manages the day-to-day relationship with our clients and the administrative compliance side of every active docket. She is the operational guarantee that every POA, every renewal window, every recordal and every form is handled correctly and on schedule.',
      education: 'IFRS, Instituto Federal do Rio Grande do Sul',
      tenure: 'Account management & INPI compliance',
      photo: '/images/team/bele.jpg',
      initials: 'GS',
      linkedin: null,
    },
  ];

  return (
    <section
      id="team"
      className={`py-20 scroll-mt-24 relative ${transparentBackground ? 'bg-transparent' : 'bg-white'}`}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-text mb-4">Our Team</h2>
          <p className="text-lg text-text/70 leading-relaxed">
            Five dedicated specialists running the marathon so your global IP filings cross the finish line.
          </p>
        </div>

        <div className="max-w-6xl mx-auto space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {leadership.map((member) => (
              <TeamMemberCard key={member.name} member={member} leadership />
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member) => (
              <TeamMemberCard key={member.name} member={member} />
            ))}
          </div>
        </div>

        <p className="max-w-4xl mx-auto mt-12 text-sm text-text/60 leading-relaxed text-center">
          TM RUSH® operates as a specialized IP firm focused on administrative prosecution before the Brazilian BPTO (INPI). Like trademark agents at the EUIPO or chartered trade mark attorneys in the UK, our IP specialists are not OAB-registered attorneys; administrative IP procedures in Brazil do not require one. For judicial enforcement matters, we work with established partner law firms.
        </p>

        {showCta && (
          <div className="text-center mt-12">
            <Link
              to="/order"
              className="inline-block bg-primary text-white px-10 py-4 rounded-xl hover:bg-primary-hover font-semibold shadow-md hover:shadow-lg"
            >
              Start Application
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Team;
